import { useId, useState } from "react";
import { MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { ClientAddress } from "@/lib/api";
import { cn } from "@/lib/utils";
import { addressDetailsLine, addressStreetLine } from "./addressFormat";

interface OrderShippingAddressSelectProps {
  addresses: ClientAddress[];
  selectedAddress: ClientAddress | null;
  onSelect: (addressId: number) => void;
  isLoading?: boolean;
  isError?: boolean;
  disabled?: boolean;
  /** Nome da escola exibido no cabeçalho ("Entrega para ..."). */
  recipientName?: string;
}

function AddressLines({ address }: { address: ClientAddress }) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold leading-snug break-words">{addressStreetLine(address)}</span>
        {address.isDefault && <Badge variant="success">Padrão</Badge>}
        {address.label && <Badge variant="outline">{address.label}</Badge>}
      </div>
      <p className="text-sm text-muted-foreground break-words">{addressDetailsLine(address)}</p>
    </div>
  );
}

/**
 * Endereço de entrega do pedido (todos os tipos), no resumo de valores acima do cupom: cartão com o endereço
 * escolhido e "Alterar", que abre a lista de endereços em cards. A previsão de entrega usa a UF do endereço escolhido.
 */
export function OrderShippingAddressSelect({
  addresses,
  selectedAddress,
  onSelect,
  isLoading = false,
  isError = false,
  disabled = false,
  recipientName,
}: OrderShippingAddressSelectProps) {
  const labelId = useId();
  const optionIdPrefix = useId();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftId, setDraftId] = useState<string>();

  const canChange = addresses.length > 1 && !disabled;

  const openDialog = () => {
    setDraftId(selectedAddress ? String(selectedAddress.id) : undefined);
    setDialogOpen(true);
  };

  const confirm = () => {
    if (draftId) onSelect(Number(draftId));
    setDialogOpen(false);
  };

  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-4" aria-labelledby={labelId} role="group">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span id={labelId} className="text-sm font-semibold">
          {recipientName ? `Entrega para ${recipientName}` : "Endereço de entrega"}
        </span>
      </div>
      {isLoading ? (
        <Skeleton className="h-14 w-full" />
      ) : isError ? (
        <p className="text-sm text-destructive">
          Não foi possível carregar os endereços de entrega. O pedido usará o endereço padrão de entrega da escola.
        </p>
      ) : addresses.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          A escola não tem endereço de entrega cadastrado. O pedido usará o endereço do cadastro.
        </p>
      ) : (
        <>
          {selectedAddress && (
            <div className="flex items-start justify-between gap-3 rounded-md border bg-background p-3">
              <AddressLines address={selectedAddress} />
              {canChange && (
                <Button type="button" variant="link" className="h-auto shrink-0 p-0" onClick={openDialog}>
                  Alterar
                </Button>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">A previsão de entrega usa a UF do endereço escolhido.</p>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Escolha onde entregar o pedido</DialogTitle>
            <DialogDescription>A previsão de entrega usa a UF do endereço escolhido.</DialogDescription>
          </DialogHeader>
          <RadioGroup
            value={draftId}
            onValueChange={setDraftId}
            aria-label="Endereços de entrega"
            className="max-h-[60vh] gap-3 overflow-y-auto pr-1"
          >
            {addresses.map((address) => {
              const value = String(address.id);
              const optionId = `${optionIdPrefix}-${value}`;
              return (
                <Label
                  key={address.id}
                  htmlFor={optionId}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-lg border p-4 font-normal transition-colors hover:bg-muted/50",
                    draftId === value && "border-primary bg-primary/5 hover:bg-primary/5",
                  )}
                >
                  <RadioGroupItem value={value} id={optionId} className="mt-1 shrink-0" />
                  <AddressLines address={address} />
                </Label>
              );
            })}
          </RadioGroup>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirm} disabled={!draftId}>
              Usar este endereço
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
